import { loanApi } from "@api/loans";
import { searchReady, withCancel } from "@api/utils";
import { invenioConfig } from "@config";
import _get from "lodash/get";
import _has from "lodash/has";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, Icon, Popup } from "semantic-ui-react";
import { DateTime } from "luxon";

const INFO_MESSAGES = {
  SUCCESS: (documentTitle, endDate) =>
    `Your loan for "${documentTitle}" has been extended. The return date has changed to ${endDate.toLocaleString(
      { month: "long", day: "numeric" }
    )}.`,
  MISSING_ACTION_URL: "It is not possible to extend this loan!",
  MAX_EXTENSIONS:
    "You have reached the max number of extensions for this loan!",
  DUE_TODAY: (endDate) =>
    `You can only request an extension on the due date (${endDate.toLocaleString(
      {
        month: "long",
        day: "numeric",
      }
    )}).`,
};

class LoanExtendButton extends Component {
  constructor(props) {
    super(props);
    this.state = { isLoading: false };
  }

  componentWillUnmount() {
    this.cancellableExtendLoan && this.cancellableExtendLoan.cancel();
  }

  async extendLoan(actionURL, documentPid, patronPid) {
    const { loan } = this.props;
    if (!loan || !loan.metadata || !loan.metadata.end_date) {
      throw new Error("Invalid loan data.");
    }

    const currentEndDate = DateTime.fromISO(loan.metadata.end_date);
    const extensionDuration = invenioConfig.CIRCULATION.extensionDurationDays;
    const newEndDate = currentEndDate.plus({ days: extensionDuration }).toISO();

    const response = await loanApi.doAction(actionURL, documentPid, patronPid, {
      new_end_date: newEndDate, // Pass the new due date to the API
    });
    await searchReady();
    return response;
  }

  triggerExtension = async () => {
    const { loan, onSuccess, onError } = this.props;
    const { document, patron_pid: patronPid } = loan.metadata;
    const extendUrl = loan.availableActions.extend;
    this.setState({ isLoading: true });

    try {
      this.cancellableExtendLoan = withCancel(
        this.extendLoan(extendUrl, document.pid, patronPid)
      );
      const response = await this.cancellableExtendLoan.promise;
      this.setState({ isLoading: false });
      const documentTitle = document.title;
      onSuccess(
        INFO_MESSAGES.SUCCESS(
          documentTitle,
          DateTime.fromISO(response.data.metadata.end_date)
        )
      );
    } catch (error) {
      this.setState({ isLoading: false });
      onError(error.response.data.message);
    }
  };

  validateMaxExtensions(loan) {
    return (
      _get(loan, "metadata.extension_count", 0) <=
      invenioConfig.CIRCULATION.extensionsMaxCount
    );
  }

  validate = (loan) => {
    const hasExtendAction = _has(loan, "availableActions.extend");
    if (!hasExtendAction)
      return { isValid: false, msg: INFO_MESSAGES.MISSING_ACTION_URL };

    //Ensure the extension can only be requested on the due date
    const currentDate = DateTime.now();
    const endDate = DateTime.fromISO(loan.metadata.end_date);
    const isDueToday = currentDate.hasSame(endDate, "day");
    if (!isDueToday) {
      return {
        isValid: false,
        msg: INFO_MESSAGES.DUE_TODAY(endDate),
      };
    }

    const maxExtensionsReached =
      _get(loan, "metadata.extension_count", 0) >
      invenioConfig.CIRCULATION.extensionsMaxCount;
    if (maxExtensionsReached)
      return {
        isValid: false,
        msg: INFO_MESSAGES.MAX_EXTENSIONS,
      };

    return { isValid: true, msg: "" };
  };

  render() {
    const { loan } = this.props;
    const { isLoading } = this.state;
    const validation = this.validate(loan);
    const isDisabled = !validation.isValid;

    return (
      <>
        {isDisabled && (
          <Popup
            content={validation.msg}
            trigger={<Icon name="info" />}
            position="top right"
          />
        )}
        <Button
          color="orange"
          size="small"
          content="Request renewal"
          disabled={isDisabled || isLoading}
          loading={isLoading}
          onClick={this.triggerExtension}
        />
      </>
    );
  }
}

LoanExtendButton.propTypes = {
  loan: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export { LoanExtendButton };
